import { useEffect, useRef, useState } from "react";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { doc, getFirestore, setDoc, getDoc, writeBatch } from "firebase/firestore";

// @mui material components
import Grid from "@mui/material/Grid";
import { grey } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";

// Ajce Hostel App React components
import MDBox from "components/MDBox";
import MDAvatar from "components/MDAvatar";

// Ajce Hostel App React example components
//import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import { CircularProgress } from "@mui/material";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import MDAlert from "components/MDAlert";
import { toYMD } from "utils/date";
import { useMaterialUIController } from "context";

import { branches, semesters } from "utils/data";

const CustomMDAvatar = styled(MDAvatar)({
  "& .MuiAvatar-img": {
    height: "100%",
  },
});

const CustomFormControl = styled(FormControl)(({ theme }) => {
  const { palette } = theme;
  return {
    "& .MuiOutlinedInput-root": {
      height: "44px",
      "& fieldset": {
        border: "1px solid #bababa",
      },
    },
    "& .MuiOutlinedInput-root.Mui-error": {
      "& fieldset": {
        border: `2px solid ${palette.error.main}`,
      },
      "&:hover fieldset": {
        border: `2px solid ${palette.error.main}`,
      },
    },
  };
});

const storage = getStorage();
const firestore = getFirestore();

const EditStudentForm = () => {
  const fileInputRef = useRef(null);
  const [isStudentDataLoading, setIsStudentDataLoading] = useState(false);
  const [fullScreenErr, setFullScreenErr] = useState(null);
  const [profileImgInfo, setProfileImgInfo] = useState({ src: null, imgName: null });
  const [err, setErr] = useState(null);
  const [isImgUploading, setIsImgUploading] = useState(false);
  const [imgUploadInfo, setImgUploadInfo] = useState({ progress: null, firebaseImgPath: null });
  const [isAdding, setIsAdding] = useState(false);
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    admissionNo: "",
    roomNo: "",
    gender: "",
    branch: "",
    sem: "",
    idProofNo: "",
    regNo: "",
    dob: "0001-01-01",
    admissionDate: "0001-01-01",
    religion: "",
    caste: "",
    bloodGrp: "",
    mobNo: "",
    landLine: "",
    fatherName: "",
    fatherJob: "",
    fatherMob: "",
    motherName: "",
    motherJob: "",
    motherMob: "",
    guardianName: "",
    guardianRelation: "",
    guardianJob: "",
    guardianMob: "",
    presentAddr: "",
    permanentAddr: "",
    grantAvail: false,
    feeReduction: false,
    imgLink: "",
  });
  const [formErr, setFormErr] = useState({
    name: false,
    admissionNo: false,
    roomNo: false,
    gender: false,
    branch: false,
    sem: false,
    dob: false,
    admissionDate: false,
    mobNo: false,
    presentAddr: false,
    permanentAddr: false,
  });

  const [uicontrol] = useMaterialUIController();

  const { admissionNo } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        if (!admissionNo) throw new Error();
        setIsStudentDataLoading(true);

        const studentRef = doc(firestore, "students", admissionNo);
        const student = await getDoc(studentRef);
        if (!student.exists()) throw new Error();

        const studentData = student.data();

        studentData.admissionDate = toYMD(studentData.admissionDate?.toDate());
        studentData.dob = toYMD(studentData.dob?.toDate());

        setProfileImgInfo((prev) => ({ ...prev, src: studentData.imgLink }));
        setFormState((prev) => ({ ...prev, ...studentData }));
      } catch (err) {
        setFullScreenErr("Student you are trying to edit not found, try again.");
        console.log(err);
      } finally {
        setIsStudentDataLoading(false);
      }
    })();
  }, [admissionNo, setIsStudentDataLoading, setFullScreenErr]);

  async function handleUpload() {
    const file = fileInputRef.current.files?.[0];
    if (!file) return setErr("Select an image first.");
    if (imgUploadInfo.firebaseImgPath) {
      return setErr(
        "Already uploaded this image, select another image if you want to upload again."
      );
    }
    const imgName = `${Date.now()}--${file.name}`;
    try {
      setIsImgUploading(true);
      const imgRef = ref(storage, `images/${imgName}`);
      const uploadTask = uploadBytesResumable(imgRef, file, {
        contentType: file.type,
      });
      uploadTask.on("state_changed", (snapshot) => {
        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setImgUploadInfo((prev) => {
          return { ...prev, progress };
        });
      });
      const snapshot = await uploadTask;
      const downloadURL = await getDownloadURL(snapshot.ref);
      if (!downloadURL) throw new Error("Unable to upload the image, try again.");
      setImgUploadInfo((prev) => ({ ...prev, firebaseImgPath: imgRef.fullPath }));
      setProfileImgInfo((prev) => ({ ...prev, src: downloadURL }));
      setFormState((prev) => {
        return { ...prev, imgLink: downloadURL };
      });
    } catch (err) {
      setErr(err.toString());
    } finally {
      setImgUploadInfo((prev) => ({ ...prev, progress: null }));
      setIsImgUploading(false);
    }
    //delete the uploaded image if was not able to get download url
  }

  async function handleSubmit(e) {
    const formErrKeys = Object.keys(formErr);
    let errMsg = null;
    formErrKeys.forEach((key) => {
      if (
        formState[key] === "" ||
        ((key === "dob" || key === "admissionDate") && formState[key] === "0001-01-01")
      ) {
        errMsg = "Fill all the required fields.";
        setFormErr((prev) => {
          prev[key] = true;
          return prev;
        });
      }
    });
    if (errMsg) return setErr(errMsg);
    try {
      setIsAdding(true);
      const student = {};
      const studentPartial = {},
        studentPartialKeys = ["admissionNo", "name", "imgLink", "roomNo", "sem", "branch"];
      Object.keys(formState).forEach((key) => {
        if (formState[key] !== "") student[key] = formState[key];
      });
      studentPartialKeys.forEach((key) => {
        if (formState[key] !== "") studentPartial[key] = formState[key];
      });
      student.dob = new Date(student.dob);
      student.dob.setHours(0, 0, 0, 0);
      student.admissionDate = new Date(student.admissionDate);
      student.admissionDate.setHours(0, 0, 0, 0);

      const batch = writeBatch(firestore);
      const studentPartialRef = doc(firestore, "students_partial", student.admissionNo);
      batch.update(studentPartialRef, studentPartial);
      const studentRef = doc(firestore, "students", student.admissionNo.toString());
      batch.update(studentRef, student);
      await batch.commit();

      navigate(`/students/profile/${formState.admissionNo}`, {
        replace: true,
      });
    } catch (err) {
      setErr("Something went wrong, try again.");
      console.error(err);
    } finally {
      setIsAdding(false);
    }
  }

  function clearErr() {
    setErr(null);
  }

  if (isStudentDataLoading || fullScreenErr) {
    return (
      <MDBox
        sx={{ height: "70vh", display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        {isStudentDataLoading && !fullScreenErr && <CircularProgress color="primary" />}
        {fullScreenErr && !isStudentDataLoading && (
          <MDAlert color="error">
            <MDTypography variant="body2" color="white">
              {fullScreenErr}{" "}
              <Link to="/students" style={{ color: "lightblue" }}>
                Go to Students List
              </Link>
            </MDTypography>
          </MDAlert>
        )}
      </MDBox>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12} lg={12}>
        <MDBox
          mb={1.5}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <CustomMDAvatar
            src={profileImgInfo.src}
            size="xxl"
            sx={{ bgcolor: grey[500], margin: "0.5rem" }}
            onClick={(e) => {
              fileInputRef.current.click();
            }}
          />
          <MDTypography variant="subtitle2">{profileImgInfo.imgName ?? ""}</MDTypography>
          <MDTypography variant="caption">
            Click the above image to add or replace the photo.
          </MDTypography>
          <MDButton
            variant="gradient"
            color={uicontrol.sidenavColor}
            sx={{ marginTop: "10px" }}
            disabled={isImgUploading}
            onClick={handleUpload}
          >
            {imgUploadInfo.progress
              ? `Uploading... ${imgUploadInfo.progress}%`
              : imgUploadInfo.firebaseImgPath
              ? "Uploaded"
              : "Upload"}
          </MDButton>
          <input
            type="file"
            style={{ display: "none" }}
            ref={fileInputRef}
            onChange={(e) => {
              if (!e.target.files[0]) return;
              if (imgUploadInfo.firebaseImgPath) {
                const imgRef = ref(storage, imgUploadInfo.firebaseImgPath);
                deleteObject(imgRef).catch((err) => {});
                setImgUploadInfo((prev) => ({ ...prev, firebaseImgPath: null }));
              }
              setProfileImgInfo(() => ({
                src: URL.createObjectURL(e.target.files[0]),
                imgName: e.target.files[0].name,
              }));
            }}
          />
        </MDBox>
      </Grid>
      <Grid py={3} container spacing={3}>
        <Grid item xs={12} md={12} lg={12}>
          <MDTypography variant="h3">General Information</MDTypography>
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            required
            fullWidth
            variant="outlined"
            label="Full Name"
            value={formState.name}
            error={formErr.name}
            onChange={(e) => {
              if (e.target.value === "") {
                setFormErr((prev) => {
                  prev.name = true;
                  return prev;
                });
              } else {
                setFormErr((prev) => {
                  prev.name = false;
                  return prev;
                });
              }
              setFormState((prev) => {
                return { ...prev, name: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            fullWidth
            variant="outlined"
            type="email"
            label="Email"
            value={formState.email}
            onChange={(e) => {
              setFormState((prev) => {
                return { ...prev, email: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            required
            fullWidth
            variant="outlined"
            label="Admission No"
            value={formState.admissionNo}
            error={formErr.admissionNo}
            onChange={(e) => {
              if (e.target.value === "") {
                setFormErr((prev) => {
                  return { ...prev, admissionNo: true };
                });
              } else {
                setFormErr((prev) => {
                  prev.admissionNo = false;
                  return prev;
                });
              }
              setFormState((prev) => {
                return { ...prev, admissionNo: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            required
            fullWidth
            variant="outlined"
            label="Room No"
            value={formState.roomNo}
            error={formErr.roomNo}
            onChange={(e) => {
              if (e.target.value === "") {
                setFormErr((prev) => {
                  prev.roomNo = true;
                  return prev;
                });
              } else {
                setFormErr((prev) => {
                  prev.roomNo = false;
                  return prev;
                });
              }
              setFormState((prev) => {
                return { ...prev, roomNo: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <CustomFormControl fullWidth required size="medium" error={formErr.gender}>
            <InputLabel id="gender-select-label">Gender</InputLabel>
            <Select
              labelId="gender-select-label"
              id="gender-select"
              label="Gender"
              value={formState.gender}
              onChange={(e) => {
                if (e.target.value !== "") {
                  setFormErr((prev) => {
                    prev.gender = false;
                    return prev;
                  });
                }
                setFormState((prev) => {
                  return { ...prev, gender: e.target.value };
                });
              }}
            >
              <MenuItem value="m">Male</MenuItem>
              <MenuItem value="f">Female</MenuItem>
              <MenuItem value="o">Other</MenuItem>
            </Select>
          </CustomFormControl>
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          <CustomFormControl fullWidth required size="normal" error={formErr.branch}>
            <InputLabel id="branch-select-label">Branch</InputLabel>
            <Select
              labelId="branch-select-label"
              id="branch-select"
              label="Branch"
              value={formState.branch}
              onChange={(e) => {
                if (e.target.value !== "") {
                  setFormErr((prev) => {
                    prev.branch = false;
                    return prev;
                  });
                }
                setFormState((prev) => {
                  return { ...prev, branch: e.target.value };
                });
              }}
            >
              {branches.map((branch) => (
                <MenuItem value={branch}>
                  {branch !== "" ? branch : "--- No Selection ---"}
                </MenuItem>
              ))}
            </Select>
          </CustomFormControl>
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <CustomFormControl fullWidth required size="medium" error={formErr.sem}>
            <InputLabel id="sem-select-label">Semester</InputLabel>
            <Select
              labelId="sem-select-label"
              id="sem-select"
              label="Semester"
              value={formState.sem}
              error={formErr.sem}
              onChange={(e) => {
                if (e.target.value !== "") {
                  setFormErr((prev) => {
                    prev.sem = false;
                    return prev;
                  });
                }
                setFormState((prev) => {
                  return { ...prev, sem: e.target.value };
                });
              }}
            >
              {semesters.map((semester) => (
                <MenuItem value={semester}>
                  {semester !== "" ? semester : "--- No Selection ---"}
                </MenuItem>
              ))}
            </Select>
          </CustomFormControl>
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            fullWidth
            variant="outlined"
            label="ID Proof No"
            value={formState.idProofNo}
            onChange={(e) => {
              setFormState((prev) => {
                return { ...prev, idProofNo: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            fullWidth
            variant="outlined"
            label="Register No"
            value={formState.regNo}
            onChange={(e) => {
              setFormState((prev) => {
                return { ...prev, regNo: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            required
            fullWidth
            type="date"
            label="Date of Birth"
            value={formState.dob}
            error={formErr.dob}
            onChange={(e) => {
              if (e.target.value === "" || e.target.value === "0001-01-01") {
                setFormErr((prev) => {
                  return { ...prev, dob: true };
                });
              } else {
                setFormErr((prev) => {
                  prev.dob = false;
                  return prev;
                });
              }
              setFormState((prev) => {
                return { ...prev, dob: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            required
            fullWidth
            type="date"
            label="Admission Date"
            value={formState.admissionDate}
            error={formErr.admissionDate}
            onChange={(e) => {
              if (e.target.value === "" || e.target.value === "0001-01-01") {
                setFormErr((prev) => {
                  return { ...prev, admissionDate: true };
                });
              } else {
                setFormErr((prev) => {
                  prev.admissionDate = false;
                  return prev;
                });
              }
              setFormState((prev) => {
                return { ...prev, admissionDate: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            fullWidth
            label="Religion"
            value={formState.religion}
            onChange={(e) => {
              setFormState((prev) => {
                return { ...prev, religion: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            fullWidth
            label="Caste"
            value={formState.caste}
            onChange={(e) => {
              setFormState((prev) => {
                return { ...prev, caste: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            fullWidth
            label="Blood Group"
            value={formState.bloodGrp}
            onChange={(e) => {
              setFormState((prev) => {
                return { ...prev, bloodGrp: e.target.value };
              });
            }}
          />
        </Grid>
      </Grid>
      <Grid py={3} container spacing={3}>
        <Grid item xs={12} md={12} lg={12}>
          <MDTypography variant="h3">Contact Information</MDTypography>
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            required
            fullWidth
            variant="outlined"
            label="Mobile No"
            value={formState.mobNo}
            error={formErr.mobNo}
            onChange={(e) => {
              if (e.target.value === "") {
                setFormErr((prev) => {
                  prev.mobNo = true;
                  return prev;
                });
              } else {
                setFormErr((prev) => {
                  prev.mobNo = false;
                  return prev;
                });
              }
              setFormState((prev) => {
                return { ...prev, mobNo: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            fullWidth
            variant="outlined"
            label="Land Line"
            value={formState.landLine}
            onChange={(e) => {
              setFormState((prev) => {
                return { ...prev, landLine: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            fullWidth
            variant="outlined"
            label="Father's Name"
            value={formState.fatherName}
            onChange={(e) => {
              setFormState((prev) => {
                return { ...prev, fatherName: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            fullWidth
            variant="outlined"
            label="Father's Occupation"
            value={formState.fatherJob}
            onChange={(e) => {
              setFormState((prev) => {
                return { ...prev, fatherJob: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            fullWidth
            variant="outlined"
            label="Father's Mobile"
            value={formState.fatherMob}
            onChange={(e) => {
              setFormState((prev) => {
                return { ...prev, fatherMob: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            fullWidth
            variant="outlined"
            label="Mother's Name"
            value={formState.motherName}
            onChange={(e) => {
              setFormState((prev) => {
                return { ...prev, motherName: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            fullWidth
            variant="outlined"
            label="Mother's Occupation"
            value={formState.motherJob}
            onChange={(e) => {
              setFormState((prev) => {
                return { ...prev, motherJob: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            fullWidth
            variant="outlined"
            label="Mother's Mobile"
            value={formState.motherMob}
            onChange={(e) => {
              setFormState((prev) => {
                return { ...prev, motherMob: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            fullWidth
            variant="outlined"
            label="Guardian's Name"
            value={formState.guardianName}
            onChange={(e) => {
              setFormState((prev) => {
                return { ...prev, guardianName: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            fullWidth
            variant="outlined"
            label="Guardian's Relation"
            value={formState.guardianRelation}
            onChange={(e) => {
              setFormState((prev) => {
                return { ...prev, guardianRelation: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            fullWidth
            variant="outlined"
            label="Guardian's Occupation"
            value={formState.guardianJob}
            onChange={(e) => {
              setFormState((prev) => {
                return { ...prev, guardianJob: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            fullWidth
            variant="outlined"
            label="Guardian's Mobile"
            value={formState.guardianMob}
            onChange={(e) => {
              setFormState((prev) => {
                return { ...prev, guardianMob: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            required
            multiline
            rows={5}
            fullWidth
            variant="outlined"
            label="Present Address"
            value={formState.presentAddr}
            error={formErr.presentAddr}
            onChange={(e) => {
              if (e.target.value === "") {
                setFormErr((prev) => {
                  prev.presentAddr = true;
                  return prev;
                });
              } else {
                setFormErr((prev) => {
                  prev.presentAddr = false;
                  return prev;
                });
              }
              setFormState((prev) => {
                return { ...prev, presentAddr: e.target.value };
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDInput
            required
            multiline
            rows={5}
            fullWidth
            variant="outlined"
            label="Permanant Address"
            value={formState.permanentAddr}
            error={formErr.permanentAddr}
            onChange={(e) => {
              if (e.target.value === "") {
                setFormErr((prev) => {
                  prev.permanentAddr = true;
                  return prev;
                });
              } else {
                setFormErr((prev) => {
                  prev.permanentAddr = false;
                  return prev;
                });
              }
              setFormState((prev) => {
                return { ...prev, permanentAddr: e.target.value };
              });
            }}
          />
        </Grid>
      </Grid>
      <Grid py={3} container spacing={3}>
        <Grid item xs={12} md={12} lg={12}>
          <MDTypography variant="h3">Additional Information</MDTypography>
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <CustomFormControl>
            <FormLabel id="grant-availability-radio-label">Grant Availability</FormLabel>
            <RadioGroup
              row
              aria-labelledby="grant-availability-radio-label"
              name="grantAvailability"
              value={formState.grantAvail.toString()}
              onChange={(e) => {
                setFormState((prev) => {
                  return { ...prev, grantAvail: e.target.value === "true" ? true : false };
                });
              }}
            >
              <FormControlLabel value="true" control={<Radio />} label="Yes" />
              <FormControlLabel value="false" control={<Radio />} label="No" />
            </RadioGroup>
          </CustomFormControl>
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <FormControl>
            <FormLabel id="fee-reduction-radio-label">Fee Reduction</FormLabel>
            <RadioGroup
              row
              aria-labelledby="fee-reduction-radio-label"
              name="feeReduction"
              value={formState.feeReduction.toString()}
              onChange={(e) => {
                setFormState((prev) => {
                  return { ...prev, feeReduction: e.target.value === "true" ? true : false };
                });
              }}
            >
              <FormControlLabel value="true" control={<Radio />} label="Yes" />
              <FormControlLabel value="false" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>
      <Grid py={3} container spacing={1}>
        <Grid item xs={12} md={6} lg={6}>
          <MDButton
            fullWidth
            variant="gradient"
            color="secondary"
            sx={{ height: "45px" }}
            disabled={isAdding || isImgUploading}
            onClick={() => {
              navigate("/dashboard", {
                replace: true,
              });
            }}
          >
            Cancel
          </MDButton>
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDButton
            fullWidth
            variant="gradient"
            color={uicontrol.sidenavColor}
            onClick={handleSubmit}
            sx={{ height: "45px" }}
            disabled={isAdding || isImgUploading}
          >
            {isAdding ? <CircularProgress color="dark" /> : "Save Changes"}
          </MDButton>
        </Grid>
      </Grid>
      <MDSnackbar
        color="error"
        icon="error"
        title="AJCE Hostel Management"
        dateTime=""
        content={err ?? ""}
        open={!!err}
        onClose={clearErr}
        close={clearErr}
        bgWhite
      />
    </Grid>
  );
};

export default EditStudentForm;
